import {Textarea} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TELEPHONY_CALL_NOTE_MAX_LENGTH, useSetTelephonyCallNoteMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps, useOnMount} from 'shared/web';

interface TelephonyCallNoteEditorProps extends TestIdProps {
  callId: string;
  serverNote: string | null;
  isDisabled: boolean;
}

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved';

const NOTE_SAVE_DELAY_MS = 800;

const toNoteBody = (note: string | null) => (isNotNilOrEmpty(note) ? note : null);

/**
 * The note on the call, saved a moment after the last keystroke and once more when the editor
 * goes away with a change still waiting. A note another tab saved shows once this one is idle.
 */
export function TelephonyCallNoteEditor(props: TelephonyCallNoteEditorProps) {
  const [setNote] = useSetTelephonyCallNoteMutation();
  const [draft, setDraft] = useState<string | null>(props.serverNote);
  const [lastSaved, setLastSaved] = useState<string | null>(toNoteBody(props.serverNote));
  // The server note this editor last took in; only a change of it can be another tab's write.
  const [seenServerNote, setSeenServerNote] = useState<string | null>(props.serverNote);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const pendingSaveRef = useRef<{timer: ReturnType<typeof setTimeout>; note: string | null} | null>(
    null
  );

  const save = (note: string | null) => {
    const body = toNoteBody(note);

    pendingSaveRef.current = null;
    setStatus('saving');
    setNote({callId: props.callId, body: {note: body}})
      .unwrap()
      .then(() => {
        setLastSaved(body);
        setStatus('saved');
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        setStatus('dirty');
      });
  };

  // Closing the panel or Done right after typing still saves the last keystrokes.
  useOnMount(undefined, () => {
    const pendingSave = pendingSaveRef.current;

    if (isNotNil(pendingSave)) {
      clearTimeout(pendingSave.timer);
      save(pendingSave.note);
    }
  });

  const handleChange = (note: string | null) => {
    setDraft(note);
    setStatus('dirty');

    if (isNotNil(pendingSaveRef.current)) {
      clearTimeout(pendingSaveRef.current.timer);
    }
    pendingSaveRef.current = {timer: setTimeout(() => save(note), NOTE_SAVE_DELAY_MS), note};
  };

  const isBusy = status === 'dirty' || status === 'saving';

  // A re-read lagging behind this editor's own save brings no new server note; a note that changed
  // to something this editor did not save, while it is idle, came from another tab.
  if (props.serverNote !== seenServerNote) {
    setSeenServerNote(props.serverNote);

    if (!isBusy && toNoteBody(props.serverNote) !== lastSaved) {
      setDraft(props.serverNote);
      setLastSaved(toNoteBody(props.serverNote));
    }
  }

  return (
    <VStack spacing={1}>
      <Textarea
        label={i18n.t('page.telephony.thread.noteLabel')}
        placeholder={
          props.isDisabled
            ? i18n.t('page.telephony.panel.notConnected')
            : i18n.t('page.telephony.afterCall.notePlaceholder')
        }
        value={draft}
        onChange={handleChange}
        rows={4}
        maxLength={TELEPHONY_CALL_NOTE_MAX_LENGTH}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('note', props)}
      />
      <HStack justify="flex-end">
        <Text size="xSmall" color="secondary" data-testid={suffixTestId('noteStatus', props)}>
          {match(status)
            .with('dirty', 'saving', () => i18n.t('page.telephony.panel.saving'))
            .with('saved', () => i18n.t('page.telephony.panel.saved'))
            .with('idle', () => '')
            .exhaustive()}
        </Text>
      </HStack>
    </VStack>
  );
}
